import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useThemeOptional } from '@/contexts/ThemeContext';
import { THEME_GROUPS, THEME_META } from '@/types';
import type { ThemeName } from '@/types';
import { PaletteIcon } from './Icons';

/**
 * ThemeMenu · поповер-пикер тем · slot_theme_inkteal_indigo (29 JUN 2026).
 *
 * Заменяет cycle-кнопку (на 5 тем листать по кругу неудобно). Триггер открывает
 * портал-поповер со всеми темами, сгруппированными Светлые/Тёмные, со свотчем
 * (фон + поверхность + акцент) и галочкой у активной. Клик = setTheme + закрытие.
 * Закрытие: клик вне / Escape. Используется в двух поверхностях быстрого
 * переключения: `variant="rail"` (сайдбар-foot) и `variant="topbar"`.
 *
 * useThemeOptional → рендерит null вне ThemeProvider (partial-mount фикстуры).
 */
export function ThemeMenu({ variant }: { variant: 'rail' | 'topbar' }) {
  const ctx = useThemeOptional();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number }>({ left: 12, bottom: 12 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!ctx) return null;
  const { theme, setTheme } = ctx;
  const current = THEME_META[theme];

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      if (variant === 'rail') {
        // справа от рейла, нижний край поповера = нижний край кнопки (растёт вверх)
        setCoords({ left: Math.round(r.right + 8), bottom: Math.round(window.innerHeight - r.bottom) });
      } else {
        // под кнопкой топбара, выровнен по правому краю кнопки
        setCoords({ left: Math.round(Math.max(8, r.right - 224)), top: Math.round(r.bottom + 8) });
      }
    }
    setOpen((o) => !o);
  };

  const pick = (t: ThemeName) => {
    setTheme(t);
    setOpen(false);
  };

  const trigger =
    variant === 'rail' ? (
      <button
        ref={btnRef}
        type="button"
        className=""
        data-testid="theme-toggle-widget"
        data-theme-toggle="true"
        data-current-theme={theme}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        title={`Тема: ${current.label}`}
        aria-label={`Тема: ${current.label}. Открыть выбор темы`}
      >
        <span className="rail-ic">
          <PaletteIcon />
        </span>
        <span className="rail-lab">Тема</span>
      </button>
    ) : (
      <button
        ref={btnRef}
        type="button"
        className="theme-btn"
        data-theme-toggle="true"
        data-current-theme={theme}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        title={`Тема: ${current.label}`}
        aria-label={`Тема: ${current.label}. Открыть выбор темы`}
      >
        <span className="ic">
          <PaletteIcon />
        </span>
        {current.label}
      </button>
    );

  return (
    <>
      {trigger}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popRef}
            className="theme-pop"
            role="menu"
            aria-label="Выбор темы"
            data-testid="theme-pop"
            style={{ position: 'fixed', left: coords.left, top: coords.top, bottom: coords.bottom }}
          >
            {THEME_GROUPS.map((g) => (
              <div key={g.id} className="theme-pop-group">
                <div className="theme-pop-glab">{g.label}</div>
                {g.themes.map((t) => {
                  const m = THEME_META[t];
                  const active = theme === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      data-theme-option={t}
                      className={`theme-pop-item${active ? ' on' : ''}`}
                      onClick={() => pick(t)}
                    >
                      <span
                        className="theme-sw"
                        aria-hidden="true"
                        style={{ background: m.swatch.bg, borderColor: m.swatch.accent }}
                      >
                        <span className="theme-sw-card" style={{ background: m.swatch.surface }} />
                        <span className="theme-sw-dot" style={{ background: m.swatch.accent }} />
                      </span>
                      <span className="theme-pop-nm">{m.label}</span>
                      {active && (
                        <span className="theme-pop-chk" aria-hidden="true">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
